/* The builder workspace: top bar, tool rail, one slim drawer panel, the
   canvas, and the sidebar. Phones get the rail as a bottom tab bar and the
   panel / sidebar as bottom sheets.
   0.3b: both side panels fold away (the sidebar to a slim strip), Focus mode
   leaves just the canvas, and panels stay in the page while closed, so
   reopening one finds it exactly as it was (typed text, scroll position). */

import { useEffect, useRef, useState } from 'react';
import { requestedCount } from '@gsb/core';
import { useStore, build, openPanel, toast, setSideHidden, setFocus, type PanelKey } from '../store/store';
import { BottomSheet, Toasts } from '../ui/primitives';
import { Icon } from '../ui/icons';
import { usePreflight, canOrder } from '../ui/Panels';
import { useMedia, hasFiles } from '../ui/files';
import { OrderFlow } from '../screens/Order';
import { TopBar } from './TopBar';
import { Rail } from './Rail';
import { PanelView } from './panels';
import { CanvasArea } from './CanvasArea';
import { Sidebar } from './Sidebar';
import { ReviewDialog } from './ReviewDialog';
import { StartDialog } from './StartDialog';
import { PhoneEditBar } from './SelectionTools';

const TITLES = { upload: 'Upload', designs: 'Designs', text: 'Add text', nest: 'Auto nest', bg: 'Background removal', settings: 'Settings' } as const;

export function Workspace() {
  const panel = useStore(s => s.panel);
  const sideOpen = useStore(s => s.sideOpen);
  const sideHidden = useStore(s => s.sideHidden);
  const focus = useStore(s => s.focus);
  const reviewOpen = useStore(s => s.reviewOpen);
  const startOpen = useStore(s => s.startOpen);
  const startFrom = useStore(s => s.startFrom);
  const projectId = useStore(s => s.projectId);
  const selCount = useStore(s => s.selection.length);
  const phone = useMedia('(max-width: 759px)');
  const pf = usePreflight();
  const [ordering, setOrdering] = useState(false);
  const fileOver = useFileOver();
  const visited = useVisited(panel);

  const openReview = async () => {
    const s = useStore.getState();
    if (!s.doc.sheets.some(x => x.items.length)) {
      if (requestedCount(s.doc) > 0 && s.doc.designs.length) {
        await build('build');
        if (!useStore.getState().doc.sheets.some(x => x.items.length)) return;
      } else {
        toast('Add a design to your gangsheet first.', { bad: true });
        openPanel(s.doc.designs.length ? 'designs' : 'upload');
        return;
      }
    }
    useStore.setState({ reviewOpen: true, sideOpen: false, focus: false, panel: phone ? null : useStore.getState().panel });
  };
  const startOrder = () => {
    const ok = canOrder(pf, useStore.getState().doc);
    if (!ok.ok) {
      toast((pf?.errors ? 'There ' + (pf.errors === 1 ? 'is 1 thing' : 'are ' + pf.errors + ' things') + ' to fix before we print this. ' : '') + (ok.why || ''), { bad: true, ms: 6000 });
      return;
    }
    useStore.setState({ reviewOpen: false });
    setOrdering(true);
  };
  const review = () => void openReview();
  const panels = (keys: PanelKey[]) => keys.map(k => <div key={k} className="pnl-slot" hidden={k !== panel}><PanelView panel={k} /></div>);

  return (
    <div className={'ws' + (panel && !phone && !focus ? ' has-drawer' : '') + (focus ? ' is-focus' : '') + (sideHidden ? ' side-hidden' : '')}>
      <TopBar onReview={review} />
      <div className="ws-body">
        {/* phones: while something is selected, the edit bar takes the place of the bottom tabs (0.3e) */}
        {phone && selCount > 0 && !sideOpen ? <PhoneEditBar /> : <Rail />}
        {!phone && visited.length > 0 && <aside className="drawer" hidden={!panel || focus} aria-label={panel ? TITLES[panel] : 'Tools'}>{panels(visited)}</aside>}
        <main className="ws-main">
          <CanvasArea onReview={review} />
          {focus && (
            <button className="focus-exit" onClick={() => setFocus(false)} data-tip="Show the panels again (F)" data-tip-pos="left">
              <Icon name="unfocus" />Exit focus
            </button>
          )}
          <Toasts className="in-ws" />
        </main>
        {!phone && <aside className="aside" hidden={sideHidden || focus} aria-label="Your gangsheet"><Sidebar onReview={review} /></aside>}
        {!phone && sideHidden && !focus && <SideStrip onReview={review} />}
      </div>
      {phone && visited.length > 0 && <BottomSheet open={!!panel && !focus} label={panel ? TITLES[panel] : 'Tools'} className="bs-panel" onClose={() => openPanel(null)}>{panels(visited)}</BottomSheet>}
      {phone && sideOpen && <BottomSheet label="Your gangsheet" className="bs-side" onClose={() => useStore.setState({ sideOpen: false })}><Sidebar onReview={review} /></BottomSheet>}
      {reviewOpen && <ReviewDialog onClose={() => useStore.setState({ reviewOpen: false })} onOrder={startOrder} />}
      {ordering && <OrderFlow onClose={() => setOrdering(false)} />}
      {startOpen && (!projectId || startFrom) && <StartDialog />}
      {fileOver && <div className="file-over" aria-hidden="true"><div><Icon name="upload" /><b>Drop to add your designs</b></div></div>}
    </div>
  );
}

/* The sidebar folded away: a slim strip that says what's there and opens it again. */
function SideStrip({ onReview }: { onReview: () => void }) {
  const sheets = useStore(s => s.doc.sheets.length);
  const sel = useStore(s => s.selection.length);
  const pf = usePreflight();
  const issues = pf?.issues.filter(i => i.level === 'error' || i.level === 'warning').length || 0;
  const show = () => setSideHidden(false);
  return (
    <nav className="side-strip" aria-label="Your gangsheet (folded away)">
      <button className="ibtn" aria-label="Show the sheet panel" data-tip="Show the sheet panel" data-tip-pos="left" onClick={show}><Icon name="panel" /></button>
      <span className="ss-sep" />
      <button className="ss-item" onClick={show} aria-label={sheets + (sheets === 1 ? ' sheet' : ' sheets')} data-tip="Gang sheets" data-tip-pos="left">
        <Icon name="sheets" /><b className="num">{sheets}</b>
      </button>
      {sel > 0 && (
        <button className="ss-item is-on" onClick={show} aria-label={'Edit the ' + sel + ' selected'} data-tip="Edit the selected design" data-tip-pos="left">
          <Icon name="edit" /><b className="num">{sel}</b>
        </button>
      )}
      {issues > 0 && (
        <button className="ss-item is-warn" onClick={onReview} aria-label={issues + ' to check'} data-tip="Things to check before ordering" data-tip-pos="left">
          <Icon name="alert" /><b className="num">{issues}</b>
        </button>
      )}
    </nav>
  );
}

/** the panels opened so far (kept in the page while closed) */
function useVisited(panel: PanelKey | null): PanelKey[] {
  const [list, setList] = useState<PanelKey[]>(panel ? [panel] : []);
  useEffect(() => { if (panel && !list.includes(panel)) setList(l => [...l, panel]); }, [panel]);
  return panel && !list.includes(panel) ? [...list, panel] : list;
}

/** true while files are dragged over the window */
function useFileOver() {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  useEffect(() => {
    const enter = (e: DragEvent) => { if (!hasFiles(e)) return; depth.current++; setOver(true); };
    const leave = () => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); };
    const done = () => { depth.current = 0; setOver(false); };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', done);
    window.addEventListener('dragend', done);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragleave', leave); window.removeEventListener('drop', done); window.removeEventListener('dragend', done); };
  }, []);
  return over;
}
